import { Terminal } from 'lucide-react';

import Civo from '@/assets/ico/vendor/civo.svg?c';
import Akamai from '@/assets/ico/vendor/akamai.svg?c';
import Digitalocean from '@/assets/ico/vendor/digitalocean.svg?c';
import Googlecloud from '@/assets/ico/vendor/googlecloud.svg?c';
import Aws from '@/assets/ico/vendor/aws.svg?c';
import Azure from '@/assets/ico/vendor/azure.svg?c';
import Omni from '@/assets/ico/vendor/omni.svg?c';
import { usePublicSettings } from '@/react/portainer/settings/queries';

import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';

import { CredentialType } from '../types';

type Props = {
  value: CredentialType;
  onChange: (credentialType: CredentialType) => void;
};

const providerOptions: BoxSelectorOption<CredentialType>[] = [
  {
    id: CredentialType.OMNI,
    icon: Omni,
    label: 'Sidero Omni',
    description: 'Sidero Omni Kubernetes management platform',
    value: CredentialType.OMNI,
    iconType: 'logo',
  },
  {
    id: CredentialType.CIVO,
    icon: Civo,
    label: 'Civo',
    description: 'Civo Kubernetes',
    value: CredentialType.CIVO,
    iconType: 'logo',
  },
  {
    id: CredentialType.LINODE,
    icon: Akamai,
    label: 'Akamai Connected Cloud',
    description: 'Linode Kubernetes Engine (LKE)',
    value: CredentialType.LINODE,
    iconType: 'logo',
  },
  {
    id: CredentialType.DIGITAL_OCEAN,
    icon: Digitalocean,
    label: 'DigitalOcean',
    description: 'DigitalOcean Kubernetes (DOKS)',
    value: CredentialType.DIGITAL_OCEAN,
    iconType: 'logo',
  },
  {
    id: CredentialType.GOOGLE_CLOUD,
    icon: Googlecloud,
    label: 'Google Cloud',
    description: 'Google Kubernetes Engine (GKE)',
    value: CredentialType.GOOGLE_CLOUD,
    iconType: 'logo',
  },
  {
    id: CredentialType.AWS,
    icon: Aws,
    label: 'Amazon Web Services (AWS)',
    description: 'Elastic Kubernetes Service (EKS)',
    value: CredentialType.AWS,
    iconType: 'logo',
  },
  {
    id: CredentialType.AZURE,
    icon: Azure,
    label: 'Microsoft Azure',
    description: 'Azure Kubernetes Service (AKS)',
    value: CredentialType.AZURE,
    iconType: 'logo',
  },
  {
    id: CredentialType.SSH,
    icon: Terminal,
    iconType: 'badge',
    label: 'SSH',
    description:
      'Provision a Kubernetes cluster and install Portainer using SSH',
    value: CredentialType.SSH,
  },
];

export function CloudProviderSelector({ value, onChange }: Props) {
  const { data: publicSettings } = usePublicSettings();
  const isFIPSMode = !!publicSettings?.FIPSMode;

  // Create dynamic options with cloud providers disabled when FIPS mode is enabled (only SSH remains available)
  const dynamicProviderOptions = providerOptions.map((option) => {
    if (option.id !== CredentialType.SSH) {
      return {
        ...option,
        disabled: () => isFIPSMode,
        tooltip: () =>
          isFIPSMode ? 'FIPS mode restricts access to cloud providers' : '',
      };
    }
    return option;
  });

  return (
    <BoxSelector
      radioName="cloudProvider"
      options={dynamicProviderOptions}
      value={value}
      onChange={onChange}
    />
  );
}
